import { FILTERS_NAME } from '@va/constants';
import {
  useAiChannelFilter,
  useBrowserNameFilter,
  useBrowserVersionFilter,
  useCitiesFilter,
  useCompanyOrgNameFilter,
  useCompanyOrgTypeFilter,
  useCountriesFilter,
  useDeviceTypeFilter,
  useDisplayFilter,
  useIpFilter,
  useNewDirectChannelFilter,
  useNewEmailChannelFilter,
  useNewOrganicChannelFilter,
  useNewPaidChannelFilter,
  useNewReferrerChannelFilter,
  useNewSocialChannelFilter,
  usePlatformFilter,
} from '@va/dashboard/shared/filters';
import { useTranslate } from '@va/localization';
import { FilterLogicalOperators, buildNestedFiltersPage } from '@va/types/filters';
import { useCallback, useMemo } from 'react';

export const useSharedDimensionsFilters = () => {
  const ipFilter = useIpFilter();
  const osFilter = usePlatformFilter();
  const deviceTypeFilter = useDeviceTypeFilter();
  const displayResolutionFilter = useDisplayFilter();

  const cityFilter = useCitiesFilter();
  const countryFilter = useCountriesFilter();

  const browserNameFilter = useBrowserNameFilter();
  const browserVersionFilter = useBrowserVersionFilter();

  const companyOrgNameFilter = useCompanyOrgNameFilter();
  const companyOrgTypeFilter = useCompanyOrgTypeFilter();

  return useMemo(
    () => [
      countryFilter,
      browserNameFilter,
      browserVersionFilter,
      ipFilter,
      deviceTypeFilter,
      cityFilter,
      osFilter,
      displayResolutionFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
    ],
    [
      browserNameFilter,
      browserVersionFilter,
      cityFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
      countryFilter,
      deviceTypeFilter,
      displayResolutionFilter,
      ipFilter,
      osFilter,
    ],
  );
};

export const useTrafficChannelFilter = () => {
  const translate = useTranslate();

  const direct = useNewDirectChannelFilter();
  const social = useNewSocialChannelFilter();
  const organic = useNewOrganicChannelFilter();
  const paid = useNewPaidChannelFilter();
  const referrals = useNewReferrerChannelFilter();
  const email = useNewEmailChannelFilter();
  const ai = useAiChannelFilter();

  return buildNestedFiltersPage({
    label: translate('filters.labels.trafficChannel'),
    filters: [direct, email, paid, organic, social, referrals, ai],
    id: 'traffic-channel-filters',
    name: FILTERS_NAME.TRAFFIC_CHANNEL,
    childrenLogicalOperator: FilterLogicalOperators.or,
  });
};

export const useRecordingsTrafficChannelFilters = () => {
  const translate = useTranslate();

  const apiFieldNameMapper = useCallback(() => 'trafficChannels', []);

  const direct = useNewDirectChannelFilter({ apiFieldNameMapper });
  const social = useNewSocialChannelFilter({ apiFieldNameMapper });
  const organic = useNewOrganicChannelFilter({ apiFieldNameMapper });
  const paid = useNewPaidChannelFilter({ apiFieldNameMapper });
  const referrals = useNewReferrerChannelFilter({ apiFieldNameMapper });
  const email = useNewEmailChannelFilter({ apiFieldNameMapper });
  const ai = useAiChannelFilter({ apiFieldNameMapper });

  const filters = useMemo(
    () => [direct, email, paid, organic, social, referrals, ai],
    [direct, email, organic, paid, referrals, social, ai],
  );

  return buildNestedFiltersPage({
    label: translate('filters.labels.trafficChannel'),
    filters,
    id: 'recordings-traffic-channel-filters',
    name: FILTERS_NAME.TRAFFIC_CHANNEL,
    childrenLogicalOperator: FilterLogicalOperators.or,
  });
};
